import { useEffect, useRef, useState } from "react";
import { AlertTriangle, BellRing, Siren, X } from "lucide-react";
import API from "../services/api";
import { onMessage } from "../services/wsService";
import { playNotification } from "../services/sound";
import "./BroadcastToast.css";

const STORAGE_KEY = "last_seen_broadcast_id";
const PREVIEW_EVENT = "broadcast-toast-preview";
const AUTO_DISMISS_MS = 10000;

const TOAST_META = {
  info: {
    label: "Info",
    Icon: BellRing,
  },
  warning: {
    label: "Warning",
    Icon: AlertTriangle,
  },
  urgent: {
    label: "Urgent",
    Icon: Siren,
  },
};

export default function BroadcastToast() {
  const [toasts, setToasts] = useState([]);
  const seenIdsRef = useRef(new Set());
  const toastsRef = useRef([]);
  const dismissTimersRef = useRef(new Map());

  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const lastSeen = localStorage.getItem(STORAGE_KEY);
    if (lastSeen) {
      seenIdsRef.current.add(lastSeen);
    }

    let pollInterval = null;

    const handleIncoming = (payload) => {
      const data = payload?.data && payload.data.message ? payload.data : payload;
      if (!data || !data.id || !data.message) return;
      if (seenIdsRef.current.has(data.id)) return;
      if (toastsRef.current.some((item) => item.id === data.id)) return;

      setToasts((prev) => [...prev, { ...data, removing: false }]);

      const timeout = window.setTimeout(() => {
        handleDismiss(data.id);
      }, AUTO_DISMISS_MS);
      dismissTimersRef.current.set(data.id, timeout);
    };

    const fetchLatest = async () => {
      try {
        const res = await API.get("/broadcasts", { params: { limit: 5 } });
        const list = res?.data?.data || [];
        const unseen = list.filter(
          (item) =>
            item?.id &&
            !seenIdsRef.current.has(item.id) &&
            !toastsRef.current.some((toast) => toast.id === item.id),
        );

        unseen.slice().reverse().forEach(handleIncoming);
      } catch {
        return;
      }
    };

    fetchLatest();
    pollInterval = setInterval(fetchLatest, 60000);

    const off = onMessage("broadcast", (payload) => {
      handleIncoming(payload);
      try {
        playNotification();
      } catch {}
    });
    const handlePreview = (event) => {
      handleIncoming(event?.detail);
    };
    const dismissTimers = dismissTimersRef.current;
    window.addEventListener(PREVIEW_EVENT, handlePreview);

    return () => {
      off?.();
      if (pollInterval) clearInterval(pollInterval);
      window.removeEventListener(PREVIEW_EVENT, handlePreview);
      dismissTimers.forEach((timeout) => window.clearTimeout(timeout));
      dismissTimers.clear();
    };
  }, []);

  const handleDismiss = (id) => {
    if (!id) return;
    seenIdsRef.current.add(id);
    localStorage.setItem(STORAGE_KEY, id);
    const timeout = dismissTimersRef.current.get(id);
    if (timeout) {
      window.clearTimeout(timeout);
      dismissTimersRef.current.delete(id);
    }

    setToasts((prev) =>
      prev.map((toast) => (toast.id === id ? { ...toast, removing: true } : toast)),
    );

    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 220);
  };

  if (toasts.length === 0) return null;

  return (
    <div className="broadcast-toast-stack">
      {toasts.map((toast) => {
        const toastType = TOAST_META[toast.type] ? toast.type : "info";
        const { label, Icon } = TOAST_META[toastType];

        return (
          <div
            key={toast.id}
            className={`broadcast-toast ${toastType}${toast.removing ? " broadcast-toast-removing" : ""}`}
            role="status"
            aria-live={toastType === "urgent" ? "assertive" : "polite"}
          >
            <span className="broadcast-icon-wrap">
              <Icon size={18} />
            </span>
            <div className="broadcast-content">
              <span className="broadcast-label">{label}</span>
              <p>{toast.message}</p>
            </div>
            <button
              className="broadcast-close"
              onClick={() => handleDismiss(toast.id)}
              aria-label="Dismiss broadcast"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
